import React from 'react';
import { List, Typography, Card, Divider, Tag, Space, Row, Col, Popover } from 'antd';
import {
    TrophyOutlined,
    ClockCircleOutlined,
    FlagOutlined,
    StarOutlined,
    GiftOutlined,
    WarningOutlined,
    StopOutlined
} from '@ant-design/icons';
import styles from './index.module.css';
import { parseMarkdownLinks } from './utils';

const { Title, Paragraph, Text } = Typography;

const Rule1: React.FC = () => {
    const mainTrackData = [
        '2025 年 08 月 16 日 04:00 更新的深境螺旋 12 层上半三间连打竞速',
        '每间最多 4 次时停，超出一次最终成绩罚时 1s',
        '主赛道为严格上半三间连打，禁止使用拼接',
        '每名参赛者或每个 UID 至多参与 2 条主赛道',
        '总耗时不超过 360s，且单间不超过 120s 视为有效成绩'
    ];

    const phantomTrackData = [
        '2025 年 08 月 06 日 10:00 更新的幽境危战难度 6 通关（不区分关卡）',
        '禁止任何时停操作（例如打开邮件）',
    ];

    const overallRules = [
        '所有赛道必须携带并使用烟绯',
        '伤害占比计算规则：视频中能看到的烟绯伤害数字总和 / 怪物血量',
        '相同成绩以投稿时间更早进行排序'
    ];

    const overallReqs = [
        '参赛作品需注明赛道以及成本数，话题选择需携带「灼灼杯」；',
        '投稿视频必须全程完整露出 UID，且不支持联合投稿、代投，投稿人即为参赛者',
        '投稿视频需至少保留至比赛结束 7 日内',
        '投稿完成后在评论区 [@烟绯三月夏扬州](https://space.bilibili.com/482036178) 且回复投稿后视为完成投稿；[网站上展示出投稿信息](https://www.yanfeifei.com/zhuozhuo) 视为审核完成；'
    ];

    const mainTrackReqs = [
        '视频至少从选择「深秘降福」开始，且至少以出现「与此同时，另外一边」为结尾',
        '第二间第三间开始时展示历史「深秘降福」',
        '第三间结束时，需直接退出深渊录入角色面板、命座、武器以及圣遗物面板，要求大世界队伍即为深渊队伍，此段录制不得经过拼接',
        '烟绯伤害应为全队最高，且占比不低于 40%'
    ];

    const phantomTrackReqs = [
        '视频从战斗倒计时开始，以完成挑战后录入队伍信息（同上）为结尾',
        '烟绯伤害占比不低于 30%'
    ];

    const characterCosts = [
        '四星角色不计成本',
        '限定五星角色本体计算 1 成本，后续每命座各计 1 成本',
        '常驻五星角色本体计算 0.5 成本，后续每命座各计 0.5 成本'
    ];

    const weaponCosts = [
        '限定五星武器每精炼计 1 成本',
        '常驻五星武器本体计 1 成本，精炼不计成本',
        '限定四星武器不论精炼计 0.5 成本，其他四星武器不计成本'
    ];

    const blessingCosts = [
        '选择任意有效 Buff 均记为 1 成本',
        '选择条件 Buff 后主动说明全程未触发后且审核通过后，不计成本'
    ];

    return (
        <div>
            {/* 头部标题区域 */}
            <Card className={styles.headerCard}>
                <div className={styles.headerContent}>
                    <Title level={1} className={styles.mainTitle}>
                        第一届灼灼杯开赛公告
                    </Title>
                </div>
                <Paragraph className={styles.contactInfo}>
                    <Text strong>参赛交流群：</Text>
                    <Popover
                        content={
                            <div style={{ textAlign: 'center' }}>
                                <img 
                                    src="/zhuozhuo/group.jpg" 
                                    alt="灼灼杯交流群二维码"
                                    style={{ 
                                        maxWidth: '250px', 
                                        maxHeight: '300px',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                    }} 
                                />
                                <div style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
                                    扫码加入交流群
                                </div>
                            </div>
                        }
                        title="【烟绯】灼灼杯"
                        trigger="hover"
                        placement="bottom"
                    >
                        <Tag color="blue" style={{ cursor: 'pointer' }}>1016648426</Tag>
                    </Popover>
                </Paragraph>
            </Card>

            <Row gutter={[24, 24]} className={styles.topRow}>
                {/* 开赛时间 */}
                <Col xs={24} lg={12}>
                    <Card className={styles.timeCard} title={
                        <Space>
                            <ClockCircleOutlined />
                            <span>开赛时间</span>
                        </Space>
                    }>
                        <div className={styles.timeItem}>
                            <Text strong className={styles.trackLabel}>幽境赛道：</Text>
                            <Paragraph className={styles.timeText}>
                                2025 年 08 月 06 日 10:00 ~ 2025 年 09 月 10 日 03:59
                            </Paragraph>
                        </div>
                        <Divider />
                        <div className={styles.timeItem}>
                            <Text strong className={styles.trackLabel}>主赛道：</Text>
                            <Paragraph className={styles.timeText}>
                                2025 年 08 月 16 日 04:00 ~ 2025 年 09 月 10 日 03:59
                            </Paragraph>
                        </div>
                    </Card>
                </Col>

                {/* 赛道设置 */}
                <Col xs={24} lg={12}>
                    <Card className={styles.trackCard} title={
                        <Space>
                            <FlagOutlined />
                            <span>赛道设置</span>
                        </Space>
                    }>
                        <div className={styles.trackSection}>
                            <Text strong className={styles.sectionTitle}>主赛道</Text>
                            <div className={styles.trackTags}>
                                <Tag color="red">0 成本赛道</Tag>
                                <Tag color="orange">6 成本赛道</Tag>
                                <Tag color="gold">12 成本赛道</Tag>
                                <Tag color="green">无限制赛道</Tag>
                            </div>
                        </div>
                        <Divider />
                        <div className={styles.trackSection}>
                            <Text strong className={styles.sectionTitle}>幽境赛道</Text>
                            <div className={styles.trackTags}>
                                <Tag color="purple">竞速赛道</Tag>
                                <Tag color="cyan">低成本赛道</Tag>
                            </div>
                            <Paragraph className={styles.trackDesc}>
                                低成本赛道：成本越低，排名越高，且总成本不超过 10
                            </Paragraph>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* 比赛规则 */}
            <Card className={styles.rulesCard} title={
                <Space>
                    <StarOutlined />
                    <span>比赛规则</span>
                </Space>
            }>
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={8}>
                        <Card type="inner" title="整体规则" className={styles.innerCard}>
                            <List
                                size="small"
                                dataSource={overallRules}
                                renderItem={(item, index) => (
                                    <List.Item className={styles.ruleItem}>
                                        <span className={styles.ruleNumber}>{index + 1}</span>
                                        <span>{parseMarkdownLinks(item)}</span>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card type="inner" title="主赛道规则" className={styles.innerCard}>
                            <List
                                size="small"
                                dataSource={mainTrackData}
                                renderItem={(item, index) => (
                                    <List.Item className={styles.ruleItem}>
                                        <span className={styles.ruleNumber}>{index + 1}</span>
                                        <span>{parseMarkdownLinks(item)}</span>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card type="inner" title="幽境赛道规则" className={styles.innerCard}>
                            <List
                                size="small"
                                dataSource={phantomTrackData}
                                renderItem={(item, index) => (
                                    <List.Item className={styles.ruleItem}>
                                        <span className={styles.ruleNumber}>{index + 1}</span>
                                        <span>{parseMarkdownLinks(item)}</span>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>

            {/* 稿件要求 */}
            <Card className={styles.requirementsCard} title={
                <Space>
                    <TrophyOutlined />
                    <span>稿件要求</span>
                </Space>
            }>
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <Card type="inner" title="整体要求" className={styles.innerCard}>
                            <List
                                size="small"
                                dataSource={overallReqs}
                                renderItem={(item, index) => (
                                    <List.Item className={styles.requirementItem}>
                                        <span className={styles.reqNumber}>{index + 1}</span>
                                        <span>{parseMarkdownLinks(item)}</span>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Card type="inner" title="主赛道要求" className={styles.innerCard}>
                            <List
                                size="small"
                                dataSource={mainTrackReqs}
                                renderItem={(item, index) => (
                                    <List.Item className={styles.requirementItem}>
                                        <span className={styles.reqNumber}>{index + 1}</span>
                                        <span>{parseMarkdownLinks(item)}</span>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Card type="inner" title="幽境赛道要求" className={styles.innerCard}>
                            <List
                                size="small"
                                dataSource={phantomTrackReqs}
                                renderItem={(item, index) => (
                                    <List.Item className={styles.requirementItem}>
                                        <span className={styles.reqNumber}>{index + 1}</span>
                                        <span>{parseMarkdownLinks(item)}</span>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>

            {/* 成本计算 */}
            <Card className={styles.costCard} title={
                <Space>
                    <StarOutlined />
                    <span>成本计算</span>
                </Space>
            }>
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={8}>
                        <Card type="inner" title="角色成本" className={`${styles.innerCard} ${styles.costInner}`}>
                            <List
                                size="small"
                                dataSource={characterCosts}
                                renderItem={(item, index) => (
                                    <List.Item className={styles.costItem}>
                                        <span className={styles.costBullet}>•</span>
                                        <span>{parseMarkdownLinks(item)}</span>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card type="inner" title="武器成本" className={`${styles.innerCard} ${styles.costInner}`}>
                            <List
                                size="small"
                                dataSource={weaponCosts}
                                renderItem={(item, index) => (
                                    <List.Item className={styles.costItem}>
                                        <span className={styles.costBullet}>•</span>
                                        <span>{parseMarkdownLinks(item)}</span>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card type="inner" title="「深秘降福」成本" className={`${styles.innerCard} ${styles.costInner}`}>
                            <List
                                size="small"
                                dataSource={blessingCosts}
                                renderItem={(item, index) => (
                                    <List.Item className={styles.costItem}>
                                        <span className={styles.costBullet}>•</span>
                                        <span>{parseMarkdownLinks(item)}</span>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>

            {/* 数帧规则 */}
            <Card className={styles.frameCard} title="关于数帧">
                <div className={styles.frameContent}>
                    <Paragraph className={styles.frameIntro}>
                        <Text strong>幽境赛道</Text>不支持数帧，以官方秒数作为最终成绩。
                        <Text strong>主赛道</Text>数帧规则如下：
                    </Paragraph>
                    <List
                        className={styles.frameRules}
                        dataSource={[
                            '统一 30 帧数帧，计时器出现计第一帧，计时器消失前的最后一帧计为最后一帧',
                            '除去明显开局 09:59 外，若触发跳秒，需要严格自证，否则不认可',
                            '若选手在开局和结尾使用时停，则无法使用数帧和判定跳秒',
                            '若因选手结尾使用时停导致计时器消失，则在时停前计时加一秒',
                            '若战斗结尾出现卡帧现象，重复帧不计入',
                            '数帧结果秒数取整，不提供帧数对拼'
                        ]}
                        renderItem={(item, index) => (
                            <List.Item className={styles.frameRuleItem}>
                                <span className={styles.frameNumber}>{index + 1}</span>
                                <span>{parseMarkdownLinks(item)}</span>
                            </List.Item>
                        )}
                    />
                </div>
            </Card>

            {/* 底部信息 */}
            <Row gutter={[16, 16]} className={styles.bottomCardsRow}>
                <Col xs={24} lg={8}>
                    <Card className={styles.rewardCard} title={
                        <Space>
                            <GiftOutlined />
                            <span>比赛奖励</span>
                        </Space>
                    }>
                        <div className={styles.rewardContent}>
                            <Paragraph>任意赛道前 3 名各可获得奖励：</Paragraph>
                            <div className={styles.rewardTags}>
                                <Tag color="gold" className={styles.rewardTag}>第1名：98元</Tag>
                                <Tag color="silver" className={styles.rewardTag}>第2名：68元</Tag>
                                <Tag color="#cd7f32" className={styles.rewardTag}>第3名：30元</Tag>
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card className={styles.warningCard} title={
                        <Space>
                            <WarningOutlined />
                            <span>不建议</span>
                        </Space>
                    }>
                        <Paragraph className={styles.warningText}>
                            不建议使用解锁帧率的辅助工具。
                        </Paragraph>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card className={styles.forbiddenCard} title={
                        <Space>
                            <StopOutlined />
                            <span>严格禁止</span>
                        </Space>
                    }>
                        <Paragraph className={styles.forbiddenText}>
                            禁止使用鼠标宏在内的其他辅助工具。
                        </Paragraph>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Rule1;

